
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowRight, Users } from 'lucide-react';
import { useLeadToClientConversion } from '@/hooks/useLeadToClientConversion';
import type { Lead } from './types';

interface ConvertToClientDialogProps {
  lead: Lead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ConvertToClientDialog: React.FC<ConvertToClientDialogProps> = ({
  lead,
  open,
  onOpenChange,
}) => {
  const [conversionNotes, setConversionNotes] = useState('');
  const navigate = useNavigate();
  const { convertLeadToClient, isConverting, conversionResult } = useLeadToClientConversion();

  const handleConvert = () => {
    convertLeadToClient({
      leadId: lead.id,
      conversionNotes: conversionNotes.trim() || undefined,
    });
  };

  // Navigate to client after successful conversion
  React.useEffect(() => {
    if (conversionResult?.success && conversionResult.client_id) {
      onOpenChange(false);
      navigate(`/clients/${conversionResult.client_id}`);
    }
  }, [conversionResult, navigate, onOpenChange]);

  const isAlreadyConverted = lead.converted_to_client_id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Convert Lead to Client
          </DialogTitle>
          <DialogDescription>
            {isAlreadyConverted ? (
              'This lead has already been converted to a client.'
            ) : (
              `Convert "${lead.name}" from a lead to an active client. This will preserve all lead data and create a new client record.`
            )}
          </DialogDescription>
        </DialogHeader>

        {isAlreadyConverted ? (
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              This lead was converted on {lead.conversion_date ? new Date(lead.conversion_date).toLocaleDateString() : 'an unknown date'}.
            </p>
            {lead.converted_to_client_id && (
              <Button
                className="mt-3"
                onClick={() => {
                  onOpenChange(false);
                  navigate(`/clients/${lead.converted_to_client_id}`);
                }}
              >
                View Client Record
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="conversion-notes">Conversion Notes (Optional)</Label>
                <Textarea
                  id="conversion-notes"
                  placeholder="Add any notes about this conversion..."
                  value={conversionNotes}
                  onChange={(e) => setConversionNotes(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>

              <div className="rounded-lg border p-3 bg-muted/50">
                <h4 className="text-sm font-medium mb-2">What will be converted:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Company information and details</li>
                  <li>• All contacts ({lead.contacts?.length || 0} contacts)</li>
                  <li>• Activity history ({lead.activities?.length || 0} activities)</li>
                  <li>• Notes and attachments</li>
                  <li>• News monitoring settings</li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleConvert} 
                disabled={isConverting}
                className="flex items-center gap-2"
              >
                {isConverting ? (
                  'Converting...'
                ) : (
                  <>
                    Convert to Client
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
